import App from 'app/app.js';
import venmoTemplate from './venmo.html';
import dataCollector from 'braintree-web/data-collector';
import venmo from 'braintree-web/venmo';

class VenmoButtonController {
  isInitialized = false;
  buttonDisabled = false;
  venmoInstance = null;
  isSupported = window.paymentDisplayVenmoFlag === 'Y';

  constructor(
    $rootScope,
    $scope,
    ShoppingCartStore,
    RouterService,
    AnalyticsService,
    ErrorLogService,
    StatsdService,
    PaymentService,
    ContentStore,
    loggerService
  ) {
    this.$rootScope = $rootScope;
    this.$scope = $scope;
    this.ShoppingCartStore = ShoppingCartStore;
    this.clientToken = ShoppingCartStore.getClientAuthorization().clientToken;
    this.RouterService = RouterService;
    this.AnalyticsService = AnalyticsService;
    this.ErrorLogService = ErrorLogService;
    this.loggerService = loggerService;
    this.StatsdService = StatsdService;
    this.PaymentService = PaymentService;
    this.ContentStore = ContentStore;
  }

  $onInit() {
    if (!this.isSupported) {
      console.debug("Browser doesn't support venmo, skipping");
      return;
    }
    const paymentInstancePromise = this.PaymentService.getPaymentInstancePromise();
    // eslint-disable-next-line promise/catch-or-return
    paymentInstancePromise.then((clientInstance, err) => {
      this.initVenmo(clientInstance, err);
    });
  }

  // eslint-disable-next-line max-lines-per-function
  initVenmo = (clientInstance, err) => {
    if (err || window.braintreeAllPaymentsFailFlag === true) {
      const paymentError = err || 'All Payments loading failed';
      this.ErrorLogService(paymentError, 'Braintree client initialization error');
      this.loggerService.error(paymentError, 'Braintree client initialization error');
      this.StatsdService.incrementCounter('BRAINTREE_CLIENT_INITIALIZATION_FAILURE');
      this.initialized();
      return;
    }
    dataCollector.create(
      {
        client: clientInstance,
        paypal: true
      },
      (dataCollectorErr, dataCollectorInstance) => {
        if (dataCollectorErr) {
          // Handle error in creation of data collector.
          console.error('dataCollectorErr', dataCollectorErr);
          return;
        }

        // At this point, you should access the deviceData value and provide it
        // to your server, e.g. by injecting it into your form as a hidden input.
        console.debug('Got device data:', dataCollectorInstance.deviceData);

        venmo.create(
          {
            client: clientInstance,
            allowDesktop: true,
            mobileWebFallBack: true,
            paymentMethodUsage: 'single_use' // available in v3.77.0+
            // Add allowNewBrowserTab: false if your checkout page does not support relaunching in a new tab when returning from the Venmo app. This can be omitted otherwise.
            // allowNewBrowserTab: false
          },
          (venmoErr, venmoInstance) => {
            if (venmoErr || window.braintreeVenmoFailFlag === true) {
              this.AnalyticsService.recordEvent(this.AnalyticsService.events.VENMO_ERROR);
              const paymentError = venmoErr || 'venmo loading failed';
              this.ErrorLogService(paymentError, 'Venmo initialization error');
              this.loggerService.error(paymentError, 'Venmo initialization error');
              this.StatsdService.incrementCounter('VENMO_INITIALIZATION_FAILURE');
              this.initialized();
              return;
            }

            // Verify browser support before proceeding.
            if (!venmoInstance.isBrowserSupported()) {
              console.warn('Browser does not support Venmo');
              this.isSupported = false;
              return;
            }
            this.venmoInstance = venmoInstance;
            this.initialized();

            // Check if tokenization results already exist. This occurs when your
            // checkout page is relaunched in a new tab. This step can be omitted
            // if allowNewBrowserTab is false.
            if (venmoInstance.hasTokenizationResult()) {
              venmoInstance.tokenize((tokenizeErr) => {
                if (err) {
                  this.handleVenmoError(tokenizeErr);
                }
              });
              return;
            }
            this.initialized();
          }
        );
      }
    );
  };

  tokenizeVenmo = () => {
    this.venmoInstance.tokenize((tokenizeErr, payload) => {
      this.proceedVenmoToken(tokenizeErr, payload);
    });
  };

  proceedVenmoToken(tokenizeErr, payload) {
    this.$rootScope.$emit('ExternalPaymentReturn');
    this.buttonDisabled = false;
    if (tokenizeErr) {
      //handleVenmoError
      this.handleVenmoError(tokenizeErr);
      return;
    }
    this.ShoppingCartStore.setPaymentMethodNonce(payload.nonce);
    this.ShoppingCartStore.setPaymentType(this.ContentStore.getEnum('PaymentType').code.VENMO);
    this.ShoppingCartStore.setVenmo(payload);
    this.goToReviewOrder();
  }

  initialized = () => {
    this.isInitialized = true;
    this.$scope.$applyAsync();
  };

  startPayment = () => {
    this.click();
    this.$rootScope.$emit('venmoClicked');
    this.AnalyticsService.recordEvent(this.AnalyticsService.events.VENMO_START);
    this.buttonDisabled = true;
    this.tokenizeVenmo();
  };

  goToReviewOrder = () => {
    this.RouterService.goToReviewOrder();
    this.$rootScope.$digest();
  };

  handleVenmoError = (error) => {
    if (error.code === 'VENMO_CANCELED') {
      this.AnalyticsService.recordEvent(this.AnalyticsService.events.VENMO_CANCEL);
      console.debug('App is not available or user aborted payment flow');
    } else if (error.code === 'VENMO_APP_CANCELED') {
      this.AnalyticsService.recordEvent(this.AnalyticsService.events.VENMO_CANCEL);
      console.debug('User canceled payment flow');
    } else if (error.code === 'FRAME_SERVICE_FRAME_CLOSED') {
      this.AnalyticsService.recordEvent(this.AnalyticsService.events.VENMO_CANCEL);
      console.debug('User canceled payment flow');
    } else {
      this.AnalyticsService.recordEvent(this.AnalyticsService.events.VENMO_ERROR);
      this.ErrorLogService(error, 'Venmo payment error');
      this.loggerService.error(error, 'Venmo payment error');
    }
    this.buttonDisabled = false;
    this.$scope.$digest();
  };
}

VenmoButtonController.$inject = [
  '$rootScope',
  '$scope',
  'ShoppingCartStore',
  'RouterService',
  'AnalyticsService',
  'ErrorLogService',
  'StatsdService',
  'PaymentService',
  'ContentStore',
  'loggerService'
];

App.component('ltVenmoButton', {
  template: venmoTemplate,
  bindings: {
    selected: '<',
    click: '&'
  },
  transclude: true,
  controller: VenmoButtonController,
  controllerAs: 'venmo'
});
